import React, { useState, useEffect, useCallback } from "react";
import {
  DollarSign,
  Clock,
  CheckCircle,
  XCircle,
  CreditCard,
  User,
  Calendar,
  // ArrowUpDown,
  // Activity,
  Mail,
  Hash,
  Wallet,
} from "lucide-react";
import { format } from "date-fns";
import { DataTable, DataTablePagination } from "@/components/ui/data-table";
import type { ColumnDef } from "@tanstack/react-table";
import { AdminFilters } from "../../components/admin/AdminFilters";
import {
  adminPayoutsService,
  PayoutRequest,
  PayoutSearchParams,
  SearchPayoutsResponse,
} from "../../services/admin/payouts.admin.service";

// Custom debounce hook for search
function useDebounce(callback: () => void, delay: number) {
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  return React.useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(callback, delay);
  }, [callback, delay]);
}

// Stat card skeleton
function StatCardSkeleton() {
  return (
    <div className="bg-transparent border border-border rounded-xl animate-pulse p-6">
      <div className="flex items-center justify-between space-x-4">
        <div className="space-y-2 flex-1">
          <div className="h-3 bg-muted rounded w-1/2"></div>
          <div className="h-8 bg-muted rounded w-1/3"></div>
        </div>
        <div className="h-12 w-12 rounded-2xl border border-border bg-transparent"></div>
      </div>
    </div>
  );
}

// Stat card component
interface StatCardProps {
  title: string;
  value: number | string;
  icon: React.ReactNode;
  color: string;
  formatter?: (value: number) => string;
}

function StatCard({ title, value, icon, color, formatter }: StatCardProps) {
  const formattedValue =
    formatter && typeof value === "number"
      ? formatter(value)
      : value.toString();
  const isWarm = color === "bg-orange-100 dark:bg-orange-500/15";

  return (
    <div className="bg-transparent border border-border rounded-xl transition-all duration-300 hover:border-[#d7785b]/40">
      <div className="p-6">
        <div className="flex items-center justify-between space-x-4">
          <div className="space-y-2 flex-1">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              {title}
            </p>
            <p className="text-3xl font-bold text-foreground">
              {formattedValue}
            </p>
          </div>
          <div
            className={`h-12 w-12 rounded-2xl flex items-center justify-center border ${
              isWarm
                ? "border-[#d7785b]/20 bg-transparent"
                : "border-border bg-transparent"
            }`}
          >
            <div className={isWarm ? "text-[#d7785b]" : "text-muted-foreground"}>
              {icon}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminPayouts() {
  const [payouts, setPayouts] = useState<PayoutRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);

  // Filter states
  const [statusFilter, setStatusFilter] =
    useState<PayoutSearchParams["status"]>();
  const [sortBy, setSortBy] = useState<PayoutSearchParams["sortBy"]>();
  const [sortOrder, setSortOrder] = useState<PayoutSearchParams["sortOrder"]>();
  const [amountRange, setAmountRange] = useState<[number, number]>([0, 1000]);
  const [createdAfter, setCreatedAfter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedPayout, setSelectedPayout] = useState<PayoutRequest | null>(
    null
  );
  const [showModal, setShowModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Stats
  const [stats, setStats] = useState({
    totalPendingRequests: 0,
    totalPaidRequests: 0,
    totalRequests: 0,
    totalPendingAmount: 0,
    totalPaidAmount: 0,
    totalAmount: 0,
    averagePayoutPerRequest: 0,
  });

  // Search payouts
  const searchPayouts = useCallback(async () => {
    setLoading(true);
    try {
      const params: PayoutSearchParams = {
        page,
        limit: pageSize,
      };

      if (statusFilter) {
        params.status = statusFilter;
      }

      if (amountRange[0] > 0 || amountRange[1] < 1000) {
        params.minAmount = amountRange[0];
        params.maxAmount = amountRange[1];
      }

      if (createdAfter) {
        params.createdAfter = createdAfter;
      }

      if (sortBy) {
        params.sortBy = sortBy;
      }

      if (sortOrder) {
        params.sortOrder = sortOrder;
      }

      if (searchQuery.trim()) {
        params.search = searchQuery.trim();
      }

      const response: SearchPayoutsResponse =
        await adminPayoutsService.getPayoutRequests(params);
      setPayouts(response.data);
      setTotal(response.pagination.total);
      setTotalPages(response.pagination.totalPages);
    } catch (error) {
      console.error("Failed to search payouts:", error);
      setPayouts([]);
      setTotal(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [
    page,
    pageSize,
    statusFilter,
    amountRange,
    createdAfter,
    sortBy,
    sortOrder,
    searchQuery,
  ]);

  // Debounced search for simple filters (search input, dropdown filters)
  const debouncedSimpleSearch = useDebounce(() => {
    setPage(1);
    searchPayouts();
  }, 1000);

  // Filter handlers
  const handleStatusFilterChange = (value: string | string[]) => {
    const newStatus = Array.isArray(value) ? value[0] : value;
    setStatusFilter(
      newStatus === "ALL"
        ? undefined
        : (newStatus as PayoutSearchParams["status"])
    );
    // Simple filter - auto-trigger with debounce
    debouncedSimpleSearch();
  };

  const handleAmountRangeChange = (newRange: [number, number]) => {
    setAmountRange(newRange);
    // Advanced filter - only update state, don't auto-trigger
  };

  const handleCreatedAfterChange = (date: string) => {
    setCreatedAfter(date);
    // Advanced filter - only update state, don't auto-trigger
  };

  const handleSortByChange = (value: string | string[]) => {
    const newSortBy = Array.isArray(value) ? value[0] : value;
    setSortBy(newSortBy as PayoutSearchParams["sortBy"]);
    // Simple filter - auto-trigger with debounce
    debouncedSimpleSearch();
  };

  const handleSortOrderChange = (value: string | string[]) => {
    const newSortOrder = Array.isArray(value) ? value[0] : value;
    setSortOrder(newSortOrder as PayoutSearchParams["sortOrder"]);
    // Simple filter - auto-trigger with debounce
    debouncedSimpleSearch();
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    // Search input - auto-trigger with debounce
    debouncedSimpleSearch();
  };

  const handleResetFilters = () => {
    setStatusFilter(undefined);
    setAmountRange([0, 1000]);
    setCreatedAfter("");
    setSortBy(undefined);
    setSortOrder(undefined);
    setSearchQuery("");
    setPage(1);
    searchPayouts();
  };

  const handleApplyFilters = () => {
    setPage(1);
    searchPayouts();
  };

  // Fetch stats
  const fetchStats = async () => {
    setStatsLoading(true);
    try {
      const statsData = await adminPayoutsService.getPayoutStats();
      setStats(statsData);
    } catch (error) {
      console.error("Failed to fetch payout stats:", error);
    } finally {
      setStatsLoading(false);
    }
  };

  // Handle pagination change
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  // Effect to fetch data when page changes
  useEffect(() => {
    searchPayouts();
  }, [page, searchPayouts]);

  // Initial load
  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle view payout details
  const handleViewPayout = async (payout: PayoutRequest) => {
    setSelectedPayout(payout);
    setShowModal(true);
    setModalLoading(false);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedPayout(null);
  };

  // Format currency
  const formatCurrency = (value: number) =>
    `$${value.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  // Get status badge color
  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case "PAID":
        return "bg-green-100 dark:bg-green-500/15 text-green-800 dark:text-green-300";
      case "PENDING":
        return "bg-yellow-100 dark:bg-yellow-500/15 text-yellow-800 dark:text-yellow-300";
      default:
        return "bg-muted text-foreground";
    }
  };

  // Remove handleUpdateStatus - admin cannot currently update payout status

  // Table columns
  const columns: ColumnDef<PayoutRequest>[] = [
    {
      id: "affiliate",
      header: "Affiliate",
      meta: { width: "w-52" },
      cell: ({ row }) => {
        const payout = row.original;
        return (
          <div className="flex items-center gap-3 min-w-0">
            {payout.affiliate.profileImage ? (
              <img
                src={payout.affiliate.profileImage}
                alt=""
                className="w-8 h-8 rounded-full object-cover shrink-0"
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-orange-100 dark:bg-orange-500/15 flex items-center justify-center shrink-0">
                <User className="w-4 h-4 text-[#d7785b]" />
              </div>
            )}
            <div className="font-medium text-foreground truncate">
              {payout.affiliate.firstName} {payout.affiliate.lastName}
            </div>
          </div>
        );
      },
    },
    {
      id: "code",
      header: "Code",
      meta: { width: "w-32" },
      cell: ({ row }) => (
        <div className="text-sm text-muted-foreground flex items-center gap-1 min-w-0">
          <Hash className="w-3 h-3 shrink-0" />
          <span className="truncate">{row.original.code}</span>
        </div>
      ),
    },
    {
      id: "brandName",
      header: "Brand Name",
      meta: { width: "w-40" },
      cell: ({ row }) => (
        <div className="flex items-center gap-1 text-sm text-foreground truncate">
          {row.original.brandName}
        </div>
      ),
    },
    {
      id: "contact",
      header: "Contact",
      meta: { width: "w-52" },
      cell: ({ row }) => (
        <div className="flex items-center gap-1 text-sm text-foreground min-w-0">
          <Mail className="w-3 h-3 text-muted-foreground shrink-0" />
          <span className="truncate">{row.original.affiliate.email}</span>
        </div>
      ),
    },
    {
      id: "amount",
      header: "Amount",
      meta: { width: "w-28" },
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          <DollarSign className="w-3 h-3" style={{ color: "#d7785b" }} />
          <span className="font-medium text-foreground">
            {formatCurrency(row.original.amount)}
          </span>
        </div>
      ),
    },
    {
      id: "status",
      header: "Status",
      meta: { width: "w-28" },
      cell: ({ row }) => (
        <div
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(
            row.original.status
          )}`}
        >
          {row.original.status === "PAID" && (
            <CheckCircle className="w-3 h-3 mr-1" />
          )}
          {row.original.status === "PENDING" && (
            <Clock className="w-3 h-3 mr-1" />
          )}
          {row.original.status}
        </div>
      ),
    },
    {
      id: "paymentMethod",
      header: "Payment Method",
      meta: { width: "w-40" },
      cell: ({ row }) => (
        <div className="flex items-center gap-1 text-sm">
          <Wallet className="w-3 h-3 text-muted-foreground" />
          <span className="text-foreground">
            {row.original.paymentDetails?.paymentMethodName || "Not selected"}
          </span>
        </div>
      ),
    },
    {
      id: "createdAt",
      header: "Created",
      meta: { width: "w-32" },
      cell: ({ row }) => (
        <div className="flex items-center gap-1 text-sm text-muted-foreground">
          <Calendar className="w-3 h-3" style={{ color: "#d7785b" }} />
          {format(new Date(row.original.createdAt), "MMM dd, yyyy")}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-semibold text-foreground mb-2">
            Payout Requests
          </h1>
          <p className="text-muted-foreground">
            Manage affiliate payout requests and payments
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 mb-8">
        {statsLoading ? (
          <>
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
          </>
        ) : (
          <>
            <StatCard
              title="Total Requests"
              value={stats.totalRequests}
              icon={<DollarSign className="w-5 h-5 text-foreground" />}
              color="bg-muted"
            />
            <StatCard
              title="Pending Amount"
              value={stats.totalPendingAmount}
              icon={<Clock className="w-5 h-5" style={{ color: "#d7785b" }} />}
              color="bg-orange-100 dark:bg-orange-500/15"
              formatter={formatCurrency}
            />
            <StatCard
              title="Paid Amount"
              value={stats.totalPaidAmount}
              icon={<CheckCircle className="w-5 h-5 text-foreground" />}
              color="bg-muted"
              formatter={formatCurrency}
            />
            <StatCard
              title="Average Payout"
              value={stats.averagePayoutPerRequest}
              icon={
                <CreditCard className="w-5 h-5" style={{ color: "#d7785b" }} />
              }
              color="bg-orange-100 dark:bg-orange-500/15"
              formatter={formatCurrency}
            />
          </>
        )}
      </div>

      {/* Filters */}
      <div className="">
        <AdminFilters
          filters={[
            {
              label: "Status",
              options: [
                { label: "All Statuses", value: "ALL" },
                { label: "Pending", value: "PENDING" },
                { label: "Paid", value: "PAID" },
              ],
              value: statusFilter || "ALL",
              onChange: handleStatusFilterChange,
            },
            {
              label: "Sort By",
              options: [
                { label: "Default", value: "" },
                { label: "Amount", value: "AMOUNT" },
                { label: "Created Date", value: "CREATED_AT" },
                { label: "Updated Date", value: "UPDATED_AT" },
                { label: "Status", value: "STATUS" },
              ],
              value: sortBy || "",
              onChange: handleSortByChange,
            },
            {
              label: "Sort Order",
              options: [
                { label: "Descending", value: "DESC" },
                { label: "Ascending", value: "ASC" },
              ],
              value: sortOrder || "DESC",
              onChange: handleSortOrderChange,
            },
          ]}
          searchPlaceholder="Search affiliates, emails, or payout codes..."
          searchValue={searchQuery}
          onSearch={handleSearchChange}
          revenueRange={{
            label: "Amount Range",
            min: 0,
            max: 1000,
            value: amountRange,
            onChange: handleAmountRangeChange,
          }}
          dateFilter={{
            label: "Created After",
            value: createdAfter,
            onChange: handleCreatedAfterChange,
          }}
          onReset={handleResetFilters}
          onApply={handleApplyFilters}
        />
      </div>

      {/* Payouts Table */}
      <div className="bg-card rounded-xl border border-border shadow-sm">
        <DataTable
          data={payouts}
          columns={columns}
          isLoading={loading}
          emptyText="No payout requests found"
          onRowClick={handleViewPayout}
        />

        {/* Pagination */}
        {totalPages > 1 && (
          <DataTablePagination
            page={page}
            totalPages={totalPages}
            total={total}
            pageSize={pageSize}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
            pageSizeOptions={[10, 20, 50, 100]}
            onPageChange={handlePageChange}
            noun="payouts"
          />
        )}
      </div>

      {/* Payout Details Modal */}
      {showModal && selectedPayout && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-card rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-card border-b border-border px-6 py-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-foreground">
                  Payout Request Details
                </h2>
                <button
                  onClick={closeModal}
                  className="text-muted-foreground hover:text-muted-foreground"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
            </div>

            {modalLoading ? (
              <div className="p-6">
                <div className="animate-pulse space-y-4">
                  <div className="h-6 bg-muted rounded w-1/3"></div>
                  <div className="h-4 bg-muted rounded w-1/2"></div>
                  <div className="h-4 bg-muted rounded w-2/3"></div>
                </div>
              </div>
            ) : (
              <div className="p-6 space-y-6">
                {/* Basic Info */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="bg-muted p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-foreground mb-3">
                      Payout Information
                    </h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Amount:</span>
                        <span className="font-medium text-foreground">
                          {formatCurrency(selectedPayout.amount)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Status:</span>
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(
                            selectedPayout.status
                          )}`}
                        >
                          {selectedPayout.status}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Code:</span>
                        <span className="font-medium text-foreground">
                          {selectedPayout.code}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Created:</span>
                        <span className="font-medium text-foreground">
                          {format(new Date(selectedPayout.createdAt), "PPP")}
                        </span>
                      </div>
                      {selectedPayout.paidAt && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Paid:</span>
                          <span className="font-medium text-foreground">
                            {format(new Date(selectedPayout.paidAt), "PPP")}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="bg-muted p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-foreground mb-3">
                      Affiliate Information
                    </h3>
                    <div className="flex items-center gap-3 mb-4">
                      {selectedPayout.affiliate.profileImage ? (
                        <img
                          src={selectedPayout.affiliate.profileImage}
                          alt=""
                          className="w-12 h-12 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-orange-100 dark:bg-orange-500/15 flex items-center justify-center">
                          <User
                            className="w-6 h-6"
                            style={{ color: "#d7785b" }}
                          />
                        </div>
                      )}
                      <div>
                        <div className="font-medium text-foreground">
                          {selectedPayout.affiliate.firstName}{" "}
                          {selectedPayout.affiliate.lastName}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {selectedPayout.affiliate.email}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Payment Methods */}
                {selectedPayout.matchingPaymentInfos.length > 0 && (
                  <div className="bg-card border border-border rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-foreground mb-4">
                      Available Payment Methods
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {selectedPayout.matchingPaymentInfos.map(
                        (info, index) => (
                          <div
                            key={index}
                            className={`p-4 border rounded-lg ${
                              selectedPayout.paymentDetails?.paymentMethodId ===
                              info.paymentMethod.id
                                ? "border-green-500 bg-green-50 dark:bg-green-500/10"
                                : "border-border"
                            }`}
                          >
                            <div className="flex items-center gap-2 mb-2">
                              <CreditCard
                                className="w-4 h-4"
                                style={{ color: "#d7785b" }}
                              />
                              <span className="font-medium text-foreground">
                                {info.paymentMethod.name}
                              </span>
                              {selectedPayout.paymentDetails
                                ?.paymentMethodId === info.paymentMethod.id && (
                                <span className="text-xs bg-green-100 dark:bg-green-500/15 text-green-800 dark:text-green-300 px-2 py-1 rounded-full">
                                  Selected
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-muted-foreground space-y-1">
                              {Object.entries(info.fieldData).map(
                                ([key, value]) => (
                                  <div
                                    key={key}
                                    className="flex justify-between"
                                  >
                                    <span className="capitalize">{key}:</span>
                                    <span className="font-medium">{value}</span>
                                  </div>
                                )
                              )}
                            </div>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                )}

                {/* Actions removed - admin cannot currently update payout status */}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
